import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NetworkStatus = ({ isConnected, walletData }) => {
  const [currentNetwork, setCurrentNetwork] = useState(null);
  const [isNetworkSupported, setIsNetworkSupported] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const supportedNetworks = [
    {
      id: 'polygon',
      name: 'Polygon Mainnet',
      chainId: 137,
      rpcUrl: 'https://polygon-rpc.com',
      blockExplorer: 'https://polygonscan.com',
      nativeCurrency: {
        name: 'MATIC',
        symbol: 'MATIC',
        decimals: 18
      },
      isMainnet: true,
      gasPrice: 'Low',
      status: 'active'
    },
    {
      id: 'mumbai',
      name: 'Mumbai Testnet',
      chainId: 80001,
      rpcUrl: 'https://rpc-mumbai.maticvigil.com',
      blockExplorer: 'https://mumbai.polygonscan.com',
      nativeCurrency: {
        name: 'MATIC',
        symbol: 'MATIC',
        decimals: 18
      },
      isMainnet: false,
      gasPrice: 'Free',
      status: 'active'
    }
  ];

  useEffect(() => {
    if (isConnected && walletData) {
      // Simulate network detection
      const network = supportedNetworks?.find(n => n?.name?.includes('Polygon'));
      setCurrentNetwork(network);
      setIsNetworkSupported(true);
    } else {
      setCurrentNetwork(null);
      setIsNetworkSupported(false);
    }
  }, [isConnected, walletData]);

  const handleNetworkSwitch = async (network) => {
    setIsSwitching(true);
    
    try {
      // Simulate network switching
      await new Promise(resolve => setTimeout(resolve, 1500));
      setCurrentNetwork(network);
      setIsNetworkSupported(true);
    } catch (error) {
      console.error('Failed to switch network:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  const getNetworkStatusColor = () => {
    if (!isConnected) return 'text-muted-foreground';
    if (isNetworkSupported) return 'text-success';
    return 'text-warning';
  };

  const getNetworkStatusIcon = () => {
    if (!isConnected) return 'WifiOff';
    if (isNetworkSupported) return 'CheckCircle';
    return 'AlertTriangle';
  };

  if (!isConnected) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-muted rounded-full mx-auto mb-4">
            <Icon name="WifiOff" size={24} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">Network Status</h3>
          <p className="text-muted-foreground">
            Connect your wallet to view network information
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Network Status</h3>
        <div className="flex items-center space-x-2">
          <Icon name={getNetworkStatusIcon()} size={16} className={getNetworkStatusColor()} />
          <span className={`text-sm font-medium ${getNetworkStatusColor()}`}>
            {isNetworkSupported ? 'Connected' : 'Switch Required'}
          </span>
        </div>
      </div>
      {currentNetwork && (
        <div className="mb-6 p-4 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                <Icon name="Zap" size={20} className="text-success" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">{currentNetwork?.name}</h4>
                <p className="text-sm text-muted-foreground">
                  Chain ID: {currentNetwork?.chainId} • Gas: {currentNetwork?.gasPrice}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span className="text-xs text-success font-medium">Active</span>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground">Supported Networks</h4>
        
        {supportedNetworks?.map((network) => {
          const isActive = currentNetwork?.id === network?.id;
          
          return (
            <div
              key={network?.id}
              className={`p-4 border rounded-lg transition-smooth ${
                isActive 
                  ? 'border-success bg-success/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                    isActive ? 'bg-success/10' : 'bg-muted'
                  }`}>
                    <Icon 
                      name="Zap" 
                      size={16} 
                      className={isActive ? 'text-success' : 'text-muted-foreground'} 
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-foreground">
                        {network?.name}
                      </span>
                      {network?.isMainnet && (
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                          Mainnet
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {network?.nativeCurrency?.symbol} • Gas: {network?.gasPrice}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {isActive ? (
                    <div className="flex items-center space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success" />
                      <span className="text-xs text-success font-medium">Connected</span>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleNetworkSwitch(network)}
                      loading={isSwitching}
                      disabled={isSwitching}
                    >
                      Switch
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-primary">Network Requirements</p>
            <p className="text-sm text-primary/80 mt-1">
              PolyNotary works on Polygon networks for fast, low-cost transactions. 
              Switch to a supported network to continue.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkStatus;